import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/chat/ChatPreferencesDialog.jsx
// Preferencias de notificaciones del chat (globales por usuario): sonidos,
// push, volumen, no molestar y horario silencioso. Persisten en backend.
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, Slider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import {
  VolumeUp as VolumeIcon, Notifications as BellIcon, DoNotDisturbOn as DndIcon,
  NightsStay as NightIcon, Message as MsgIcon, AlternateEmail as AtIcon, Call as CallIcon,
} from '@mui/icons-material';
import chatPrefs from '../../services/chatPrefs';
import soundManager from '../../services/soundManager';
import { ensurePushSubscription, removePushSubscription } from '../../services/pushClient';
import { useNotification } from '../common/GlobalNotifications';

const Row = ({ icon, label, children }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
    {icon}
    <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
    {children}
  </Stack>
);

const ChatPreferencesDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { showSuccess } = useNotification();
  const [g, setG] = useState(chatPrefs.getGlobal());

  useEffect(() => { if (open) setG({ ...chatPrefs.getGlobal() }); }, [open]);

  const save = async () => {
    await chatPrefs.updateGlobal(g);
    // Coordinar push del navegador con la preferencia
    if (g.push_enabled) ensurePushSubscription(); else removePushSubscription();
    showSuccess(t('hardcoded.components_chat_chatpreferencesdialog.preferencias_guardadas'));
    onClose();
  };

  const set = (k) => (e, v) => setG((p) => ({ ...p, [k]: typeof v === 'boolean' ? v : e.target.checked }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('hardcoded.components_chat_chatpreferencesdialog.notificaciones_del_chat')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>SONIDOS</Typography>
        <Row icon={<VolumeIcon fontSize="small" color="primary" />} label={t('hardcoded.components_chat_chatpreferencesdialog.sonidos_activados')}>
          <Switch checked={!!g.sounds_enabled} onChange={set('sounds_enabled')} />
        </Row>
        <Row icon={<MsgIcon fontSize="small" color="action" />} label={t('hardcoded.components_chat_chatpreferencesdialog.sonido_en_mensajes')}>
          <Switch checked={!!g.sound_messages} onChange={set('sound_messages')} disabled={!g.sounds_enabled} />
        </Row>
        <Row icon={<AtIcon fontSize="small" color="action" />} label={t('hardcoded.components_chat_chatpreferencesdialog.sonido_en_menciones')}>
          <Switch checked={!!g.sound_mentions} onChange={set('sound_mentions')} disabled={!g.sounds_enabled} />
        </Row>
        <Row icon={<CallIcon fontSize="small" color="action" />} label={t('hardcoded.components_chat_chatpreferencesdialog.sonido_en_llamadas')}>
          <Switch checked={!!g.sound_calls} onChange={set('sound_calls')} disabled={!g.sounds_enabled} />
        </Row>
        <Box sx={{ px: 0.5, py: 1 }}>
          <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_chat_chatpreferencesdialog.volumen')}{g.volume}%</Typography>
          <Slider size="small" value={g.volume ?? 80} min={0} max={100}
            onChange={(_, v) => setG((p) => ({ ...p, volume: v }))}
            onChangeCommitted={() => soundManager.play('message', { kindOverride: 'message' })}
            disabled={!g.sounds_enabled} />
        </Box>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>PUSH Y SILENCIO</Typography>
        <Row icon={<BellIcon fontSize="small" color="primary" />} label={t('hardcoded.components_chat_chatpreferencesdialog.notificaciones_push_del_navegador')}>
          <Switch checked={!!g.push_enabled} onChange={set('push_enabled')} />
        </Row>
        <Row icon={<DndIcon fontSize="small" color="error" />} label={t('hardcoded.components_chat_chatpreferencesdialog.no_molestar_silencia_todo')}>
          <Switch checked={!!g.dnd_enabled} onChange={set('dnd_enabled')} />
        </Row>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
          <NightIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ flex: 1 }}>{i18n.t('hardcoded.components_chat_chatpreferencesdialog.horario_silencioso')}</Typography>
          <TextField size="small" type="time" value={g.quiet_hours_start || ''}
            onChange={(e) => setG((p) => ({ ...p, quiet_hours_start: e.target.value || null }))}
            sx={{ width: 110 }} />
          <Typography variant="caption">a</Typography>
          <TextField size="small" type="time" value={g.quiet_hours_end || ''}
            onChange={(e) => setG((p) => ({ ...p, quiet_hours_end: e.target.value || null }))}
            sx={{ width: 110 }} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('hardcoded.components_chat_chatpreferencesdialog.durante_el_horario_silencioso_no')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={save}>{i18n.t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatPreferencesDialog;
